/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Rectangle } from 'pixi.js-legacy';
import { Subscription } from 'rxjs';

import { makeStack } from 'utils/basic-utils';
import { fbReporting } from 'src/app/plain-javascript/background-scripts/fb-reporting';
import { Ticket } from '@touchplan/common';

import { ComponentInterface } from '../../../canvas-common/interfaces/component.interface';
import { TicketContainer } from '@appPlanApplication/tickets/TicketContainer';
import { Component } from '@appCanvasCommon/component-system/Component';
import { EventBinder } from '../interaction-utils';
import { Interaction } from 'src/app/plain-javascript/services/interaction-blocker';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';

export class TicketDrag extends Component implements ComponentInterface {
	private _binder: EventBinder;
	private ticket: Ticket;

	constructor() {
		super();
	}
	/**
	 * The setup method is called once all properties on a component have been decorated by the ComponentManager.
	 *
	 * From this point on, you can safely use this.getParent() and this.getManager().
	 *
	 * Arguments passed into setup are passed in via the ComponentManager.add(name, component, ...args)
	 * @param {CanvasAngularLand} canvasAngularLand
	 * @param {TicketContainer} dataContainer
	 */
	public override setup(canvasAngularLand: CanvasAngularLand, dataContainer: TicketContainer) {
		this.canvasAngularLand = canvasAngularLand;
		this._binder = new EventBinder(
			this.getParent(),
			['hammer-panstart', 'hammer-holddragstart'],
			['hammer-panend', 'hammer-holddragend']
		);
		const camera = dataContainer.camera;
		const ticket = dataContainer.getData();
		this.ticket = ticket;

		let started = false;
		let dragStartAbortPoint = 'uncalled';
		let hasEverCalledDragStart = false;

		const dragService = canvasAngularLand.ticketDragService;

		let didSelectATicket = false;

		let deadZoneSubscription: Subscription;

		let whichStartFired = 'none';

		let checkGlobal = true;
		let globalData = 'undefined';

		//heuristically determine if the start event would have been for this
		function checkLastGlobalStart() {
			if (!checkGlobal) {
				return false;
			}
			if (window.lastStartEvent) {
				if (
					window.lastStartEvent.timestamp !== undefined &&
					Date.now() - window.lastStartEvent.timestamp < 20
				) {
					globalData = window.lastStartEvent.type;
				}
				checkGlobal = false;
			}
			return false;
		}

		const dragStart = (event) => {
			whichStartFired = event.type;
			hasEverCalledDragStart = true;
			dragStartAbortPoint = 'very beginning';
			// planState.actions.changeTicketViewToDefault(ticket.$id);
			dataContainer.cutInLine();
			dragStartAbortPoint = 'cut';

			let selectedTickets = canvasAngularLand.tickets.selectedTicket$.getValue().list;

			//the abort point
			if (
				!canvasAngularLand.interactionBlocker.check(Interaction.DragTicketInPlan, {
					selectedTickets,
					interactedTicket: ticket,
				})
			) {
				return true;
			}

			//access checking
			// if(planState.accessCheck.isReadOnly()){
			// 	planState.scopeSoup.readOnlyReaction();
			// 	dragStartAbortPoint = "read only check";
			// 	return true;
			// }
			// var notAdminNotRole = !planState.accessCheck.hasAccessToTicket2(ticket); //check interacted ticket
			// planState.tickets.selectedTicket$.getValue().list.forEach(t => {
			// 	notAdminNotRole = notAdminNotRole || !planState.accessCheck.hasAccessToTicket2(t); //check selected tickets
			// })
			// if(notAdminNotRole){ dragStartAbortPoint = "not admin not role"; return true; }

			const startz = camera.getScale().x;

			const startCX = camera.view.x / startz;
			const startCY = camera.view.y / startz;

			if (!selectedTickets.has(ticket.$id)) {
				canvasAngularLand.actions.unSelectTicket(selectedTickets);
			}
			if (selectedTickets.size === 0) {
				canvasAngularLand.actions.selectTicket(ticket);
				didSelectATicket = true;
			}
			//update the localList
			selectedTickets = canvasAngularLand.tickets.selectedTicket$.getValue().list;

			// planState.tickets.selectedTicket$.pipe(first()).subscribe((selection)=>{
			// 	if(!selection.list.has(ticket.$id)){planState.actions.unSelectTicket(selection.list);}
			// 	if(selection.list.size === 0){ planState.actions.selectTicket(ticket); didSelectATicket = true; }
			// })

			dragStartAbortPoint = 'after selection getting';
			dragService.dragStart(event.center, canvasAngularLand.tickets.selectedTicket$);
			dragStartAbortPoint = 'after dragService.dragStart';

			//convert new selection to old selection
			const oldTickets = canvasAngularLand.legacyTickets;
			if (oldTickets.selection.count) {
				oldTickets.selection.empty();
			}
			selectedTickets.forEach((t) => {
				const odl = oldTickets.$getRecord(t.$id);
				if (odl) {
					oldTickets.selection.add(odl);
				}
			});
			// planState.tickets.selectedTicket$.pipe(first()).subscribe((selection)=>{
			// 	var oldTickets = planState.scopeSoup.tickets;
			// 	if(oldTickets.selection.count){ oldTickets.selection.empty();	}
			// 	selection.list.forEach((t)=>{
			// 		var odl = oldTickets.$getRecord(t.$id)
			// 		if(odl){ oldTickets.selection.add(odl); }
			// 	});
			// })
			dragStartAbortPoint = 'before legacy dragStart';
			window.legacyTicketHandling.dragStart(event, null, 'tickets');
			dragStartAbortPoint = 'after legacy dragStart';
			deadZoneSubscription = window.legacyTicketHandling.doorDeadZone.subscribe((rect: Rectangle) => {
				dragService.updateDragAtEdgeDeadZones(rect ? [rect] : []);
			});
			dragStartAbortPoint = 'the end';

			started = true;
		};

		const drag = (event) => {
			// if(!started && fbReporting){
			// 	fbReporting.reportRealWarning(
			// 		"canvas ticket drag detected without a successful dragStart",
			// 		Object.assign(utils.makeStack(new Error("canvas ticket drag detected without a successful dragStart")), {
			// 			hasDestroyed: !this.planState,
			// 			ticketId: this.ticket ? this.ticket.$id : "undefined",
			// 			binderDetecting: this._binder && this._binder.detecting,
			// 			planStateState: this.planState.stateSync
			// 		})
			// 	)
			// 	return;
			// }
			if (!started) {
				checkLastGlobalStart();
				//lets just return and see what happens to all the other errors
				return;
			}
			window.legacyTicketHandling.drag(event, null);
			return dragService.drag(event.center);
		};

		const dragEnd = (event) => {
			if (!started && fbReporting) {
				fbReporting.reportRealWarning(
					'canvas ticket dragEnd detected without a successful dragStart',
					Object.assign(
						makeStack(new Error('canvas ticket dragEnd detected without a successful dragStart')),
						{
							dragEventType: whichStartFired,
							lastGlobalEventType: globalData,
							lastGlobalEventRaw: {
								type:
									window.lastStartEvent && window.lastStartEvent.type
										? window.lastStartEvent.type
										: 'undefined',
								timestamp:
									window.lastStartEvent && window.lastStartEvent.timestamp
										? window.lastStartEvent.timestamp
										: 'undefined',
								raw:
									window.lastStartEvent && window.lastStartEvent.raw
										? window.lastStartEvent.raw
										: 'undefined',
							},
							hasEverCalledDragStart,
							dragStartAbortPoint,
							hasDestroyed: !this.canvasAngularLand,
							ticketId: this.ticket ? this.ticket.$id : 'undefined',
							binderDetecting: this._binder && this._binder.detecting,
							planStateState: this.canvasAngularLand.stateSync,
							isReadOnly: canvasAngularLand.planAccessCheck.hasRestrictedAccessSync,
							isAdmin: canvasAngularLand.planAccessCheck.isAdminSync,
						}
					)
				);

				//clean up, just in case
				started = false;
				dragStartAbortPoint = 'uncalled';
				whichStartFired = 'none';
				checkGlobal = true;
				globalData = 'undefined';

				return;
			}

			//legacy handling is now expected to call the appropriate dragSevice clean up
			window.legacyTicketHandling
				.dragEnd(event)
				// if(!isHandled){ dragService.dragEnd(event.center); }
				.then(() => {
					if (!canvasAngularLand.ticketDragService.showVariancePrompt.getValue()) {
						if (didSelectATicket) {
							canvasAngularLand.actions.unSelectTicket(ticket);
						}
						didSelectATicket = false;
					}

					deadZoneSubscription.unsubscribe();
				});

			started = false;
			dragStartAbortPoint = 'uncalled';
			whichStartFired = 'none';
			checkGlobal = true;
			globalData = 'undefined';
		};

		this._binder.add('hammer-panstart', dragStart);
		this._binder.add('hammer-pan', drag);
		this._binder.add('hammer-panend', dragEnd);
		this._binder.add('hammer-holddragstart', dragStart);
		this._binder.add('hammer-holddrag', drag);
		this._binder.add('hammer-holddragend', dragEnd);
	}
	/**
	 * Clean shit up here.
	 */
	public override destroy() {
		if (this._binder.detecting) {
			this.canvasAngularLand.ticketDragService.dragCancel();
		}
		this._binder.destroy();

		this.ticket = null;
		super.destroy();
	}
}
